import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { login as apiLogin, loginWithMfa as apiLoginWithMfa, logout as apiLogout, getCurrentUser, type User } from '@/api/auth';
import { ApiError } from '@/api/client';

export type UserRole = 'global_admin' | 'global_reader' | 'channel_manager' | 'org_admin' | 'org_user';

// Re-export User type for convenience
export type { User };

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string, turnstileToken?: string) => Promise<{ requiresMfa?: boolean; mfaToken?: string }>;
  loginWithMfa: (mfaToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  hasRole: (...roles: UserRole[]) => boolean;
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      // Actions
      login: async (email, password, turnstileToken) => {
        set({ isLoading: true, error: null });

        try {
          const result = await apiLogin({ email, password, turnstile_token: turnstileToken });

          // Check if MFA is required
          if ('requires_mfa' in result && result.requires_mfa) {
            set({ isLoading: false });
            return { requiresMfa: true, mfaToken: result.mfa_token };
          }

          // Login successful (no MFA)
          const user = result as User;

          // Check if user must change password
          if (user.must_change_password) {
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
            return {};
          }

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });

          return {};
        } catch (err) {
          const message = err instanceof ApiError ? err.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      loginWithMfa: async (mfaToken, code) => {
        set({ isLoading: true, error: null });

        try {
          const user = await apiLoginWithMfa({ mfa_token: mfaToken, code });

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err) {
          const message = err instanceof ApiError ? err.message : 'MFA verification failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        try {
          await apiLogout();
        } catch {
          // Ignore logout errors
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      checkSession: async () => {
        set({ isLoading: true });

        try {
          const user = await getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      hasRole: (...roles) => {
        const { user } = get();
        if (!user) return false;
        return roles.includes(user.role);
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist minimal data for session restoration hint
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize session check on app load
if (typeof window !== 'undefined') {
  useAuthStore.getState().checkSession();
}
